import React, { useState, useEffect } from "react"
import axios from "axios"
import { Helmet } from "react-helmet"

import { useForm, Controller } from "react-hook-form"
import Case from "case"
import {
  TextField,
  FormControlLabel,
  Typography,
  RadioGroup,
  Radio,
  Button,
  Box,
  Grid,
  CircularProgress,
} from "@material-ui/core"

import { Autocomplete } from "@material-ui/lab"
import Interface from "../components/Interface"
import Layout from "../components/Layout"
import { API_URL } from "../constants"

const MAX_STEP = 3
const GROUPS = {
  1: {
    heading: "How can we help you?",
    headingAlt: "Coaching info",
    description:
      "Tell us about your situation. We'll use this info to match you with one of our coaches.",
    fields: [
      "needs_help_with",
      "why_need_coaching",
      "coaching_expectations",
      "coaching_topics",
      "job_search_type",
      "preferred_languages",
    ],
  },
  2: {
    heading: "Personal info",
    description: "Your coach will need to know a few things about you.",
    fields: [
      "nationality",
      "gender",
      "age_group",
      "employment_status",
      "industries_experience",
      "soft_skills",
      "hard_skills",
      "has_valid_work_permit",
      "is_in_interview_process",
      "is_in_the_netherlands",
    ],
  },
  3: {
    heading: "Contact details",
    description: "How can we contact you?",
    fields: [
      "first_name",
      "last_name",
      "email",
      "phone",
      "address",
      "city",
      "postal_code",
      "referral",
      "is_subscribed",
    ],
  },
}

export default function ClientRegistration({ pageContext }) {
  const {
    register,
    control,
    trigger,
    formState: { errors },
    handleSubmit,
  } = useForm()
  const [success, setSuccess] = useState(false)
  const [failure, setFailure] = useState(false)
  const [loading, setLoading] = useState(false)
  const [values, setValues] = useState(null)
  const [step, setStep] = useState(1)
  const fields = pageContext.fields

  const onSubmit = async (data) => {
    const formData = { ...data }
    Object.keys(formData).forEach((d) => {
      if (data[d] === "true" || data[d] === "Yes") {
        formData[d] = true
      }
      if (data[d] === "false" || data[d] === "No") {
        formData[d] = false
      }
    })
    setStep(step + 1)
    setValues(formData)
  }

  const submitApplication = async (e) => {
    e.preventDefault()
    setLoading(true)

    try {
      const res = await axios.post(
        `${API_URL}/custom/clients/new`,
        { status: "new", ...values },
        {
          headers: {
            Authorization: "Bearer c05fff",
          },
        }
      )
      setStep(step + 1)
      setSuccess(true)
    } catch {
      setStep(step + 1)
      setLoading(false)
      setFailure(true)
      // /
    }
  }

  const goToNextStep = async (e) => {
    e.preventDefault()

    const isValid = await trigger(GROUPS[step].fields)

    if (isValid) {
      setStep(step + 1)
    }
  }

  const goToPrevStep = (e) => {
    e.preventDefault()
    setStep(step - 1)
  }

  useEffect(() => {
    window.scroll(0, 0)

    if (step === 4 && failure) {
      setFailure(false)
    }
  }, [step])

  return (
    <Layout>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Sign up - Empowerment</title>
      </Helmet>
      <Grid container spacing={10}>
        <Grid item xs={12} md={6}>
          <Typography variant="h1" className="mb-2" style={{ width: "80%" }}>
            Welcome to <em style={{ whiteSpace: "nowrap" }}>Empowerment!</em>
          </Typography>
          <Typography variant="h2" className="mb-1">
            <em>Finding the best match is the key!</em>
          </Typography>
          <Typography className="mb-2">
            Among our team of highly trained and experienced coaches there is
            the perfect match for you and your career search. We only need a
            little more information from you.
          </Typography>
          <Typography className="mb-2">
            Please fill out the following form to help us pair you with the
            right coach.
          </Typography>
          <Typography variant="h2" className="mb-1">
            <em>Please note:</em>
          </Typography>
          <Typography className="mb-2">
            Empowerment is NOT a recruitment agency. Clients should not expect
            this service.
          </Typography>{" "}
          <Typography variant="h2" className="mb-1">
            <em>However:</em>
          </Typography>
          <Typography className="mb-2">
            We do collect additional information for potential job opportunities
            among our partnerships. Providing this information means you consent
            to us sharing it with third parties as well. We will NEVER provide
            your personal details without your consent first. In case you match
            with a role for a partner company, we will contact you directly.
          </Typography>
        </Grid>
        <Grid item xs={12} md={6}>
          <form
            className="form-container mb-2"
            // onSubmit={handleSubmit(onSubmit)}
          >
            {step <= MAX_STEP && (
              <>
                <Typography variant="h2">
                  <em>Step {step}</em> {GROUPS[step].heading}
                </Typography>
                <Box width="80%" className="mb-2">
                  <Typography variant="body2">
                    {GROUPS[step].description}
                  </Typography>
                </Box>

                {fields
                  .filter((field) => GROUPS[step].fields.includes(field.field))
                  .map((field) => {
                    return (
                      <div key={field.field} className="field-container">
                        <Typography
                          variant="subtitle1"
                          component="label"
                          htmlFor={field.field}
                        >
                          {field.meta.note || Case.sentence(field.field)}
                        </Typography>
                        <Interface
                          formField={field}
                          register={register}
                          control={control}
                          error={errors[field.field]}
                          required
                        />
                      </div>
                    )
                  })}
              </>
            )}

            {step === 4 && <FormSummary values={values} fields={fields} />}
            {success && <Success />}
            {failure && <Failure />}

            {failure && (
              <Button
                variant="contained"
                color="secondary"
                style={{ width: "100%" }}
                type="submit"
                onClick={goToPrevStep}
              >
                Previous step
              </Button>
            )}

            {step <= MAX_STEP && (
              <Button
                variant="contained"
                color="secondary"
                style={{ width: "100%" }}
                type="submit"
                className="mb-1"
                onClick={
                  step < MAX_STEP ? goToNextStep : handleSubmit(onSubmit)
                }
              >
                Next step
              </Button>
            )}

            {step === MAX_STEP + 1 && (
              <Button
                variant="contained"
                color="secondary"
                style={{ width: "100%" }}
                type="submit"
                className="mb-1"
                onClick={submitApplication}
                disabled={loading}
                size="large"
              >
                {loading ? <CircularProgress /> : "Submit application"}
              </Button>
            )}

            {step > 1 && step <= MAX_STEP + 1 && (
              <Button
                color="secondary"
                style={{ width: "100%" }}
                type="submit"
                onClick={goToPrevStep}
                disabled={loading}
              >
                Previous step
              </Button>
            )}
          </form>
        </Grid>
      </Grid>
    </Layout>
  )
}

const FormSummary = ({ values, fields }) => {
  if (!values) return null

  const mappedFields = {}
  fields.forEach((f) => {
    mappedFields[f.field] = {
      field: f.field,
      label: f.meta.note || Case.sentence(f.field),
      value: values[f.field],
    }
  })

  const renderValue = (value) => {
    if (Array.isArray(value)) {
      return value.join(", ")
    }

    if (String(value) === "true") {
      return "Yes"
    }

    if (String(value) === "false") {
      return "No"
    }

    return String(value) || "-"
  }

  return (
    <Box>
      <Typography variant="h2" className="mb-2">
        <em>Last step</em> Let's review your application
      </Typography>
      {[1, 2, 3].map((group) => (
        <Box key={group} className="mb-2">
          <Typography variant="h3">
            {GROUPS[group].headingAlt || GROUPS[group].heading}
          </Typography>
          {GROUPS[group].fields.map((fieldName) => (
            <Grid
              container
              spacing={3}
              key={fieldName}
              className="hasStripes hasHover"
              style={{ margin: 0 }}
            >
              <Grid item xs={12} lg={6}>
                <Typography variant="body2" className="text-semibold">
                  {mappedFields[fieldName].label || ""}
                </Typography>
              </Grid>
              <Grid item xs={12} lg={6}>
                <Typography variant="body2">
                  {renderValue(mappedFields[fieldName].value)}
                </Typography>
              </Grid>
            </Grid>
          ))}
        </Box>
      ))}
    </Box>
  )
}

const Success = () => {
  return (
    <Box display="flex" flexDirection="column" padding={10}>
      <Typography variant="h1" className="mb-2">
        <em>It's done</em>
      </Typography>
      <Typography>
        We've just received your application and we'll review it. You'll hear
        from us soon!
      </Typography>
    </Box>
  )
}

const Failure = () => {
  return (
    <Box display="flex" flexDirection="column" padding={10}>
      <Typography variant="h1" className="mb-2">
        Something went wrong...
      </Typography>
      <Typography>
        We couldn't process your application. It could be a problem on our end.
        Please try again and if the error persist please contact us at
        coaching@empoweramsterdam.com
      </Typography>
    </Box>
  )
}
